<template>
    <p>This is a table showing a list of assessment activation statuses</p>

    <v-row class="ma-0 ga-4 justify-space-between">
        <v-col class="px-0 flex-1-1-0" cols="12" md="6">
            <v-autocomplete
                v-model="filters.type"
                variant="outlined"
                label="Tipo de Avaliação"
                autocomplete="off"
                :items="assessmentTypesList"
            />
        </v-col>
        <v-col class="px-0 flex-1-1-0" cols="12" md="6">
            <v-autocomplete 
                v-model="filters.year" 
                variant="outlined" 
                autocomplete="off"
                label="Ano" 
                :items="yearsList"
                :loading="localIsFetchingYearsList"
                :disabled="localIsFetchingYearsList"
            />
        </v-col>
        
    </v-row>
    <v-row class="ma-0 justify-space-between align-center ga-4">
        <p>Estado:</p>
        <v-col class="px-0 flex-1-1-0" cols="12" md="8">
            <v-radio-group v-model="filters.enabled" inline hide-details>
                <v-radio label="Qualquer" :value="null"></v-radio>
                <v-radio label="Activas" :value="1"></v-radio>
                <v-radio label="Bloqueadas" :value="0"></v-radio>
            </v-radio-group>
        </v-col>
        <v-col class="px-0 flex-1-1-0 d-flex justify-end" cols="12" md="4">
            <v-btn 
                class="text-none" 
                :prepend-icon="
                    isFetchingActivationStatusesList
                    ? 'mdi-loading mdi-spin'
                    : 'mdi-filter'
                " 
                :disabled="isFetchingActivationStatusesList"
                color="primary"
                variant="outlined" 
                @click="fetchAssessmentStatusesList"
            >Filtrar</v-btn>
        </v-col>
    </v-row>
    <v-row class="mx-0 my-6"> 
        <v-col class="px-0 d-flex justify-end" cols="12">
            <v-btn class="text-none" prepend-icon="mdi-timer-lock-open-outline" color="primary" @click="openDialogForAction('create')">Ativar Avaliações</v-btn>
        </v-col>
    </v-row>
    <v-data-table-server
        v-model:page="filters.page"
        v-model:items-per-page="filters.per_page"
        :sort-by="[{key: filters.order_by, order:filters.direction}]"
        :headers="tableHeaders"
        :items-length="filters.total"
        :items="activationStatusesList"
        :loading="isFetchingActivationStatusesList"
        :loading-text="'Obtendo estados das avaliações'"
        no-data-text="Não existem estados de activação que correspondem aos filtros selecionados"
        :mobile="mdAndDown"
        hide-default-footer
        @update:sort-by="(newOrder) => handleSortingUpdate(newOrder)"
    >
        <template #item.type="{item}">
            <span v-if="item?.type">
                <v-icon >{{ 
                    item?.type === 'self' 
                    ?  "mdi-head-snowflake"
                    : (
                        item?.type === "ascending"
                        ? "mdi-account-arrow-up"
                        : "mdi-account-supervisor-circle"
                    )
                }}</v-icon>
                <span>{{ 
                    item?.type === 'self' 
                    ?  "Auto-Avaliação"
                    : (
                        item?.type === "ascending"
                        ? "Avaliação Ascendente"
                        : "Avaliação"
                    )
                }}</span>
            </span>
        </template>
        <template #item.enabled="{item}">
            <v-chip
                :prepend-icon="
                    isActive(item)
                    ? 'mdi-lock-open-variant-outline'
                    : 'mdi-lock-alert'
                "
                :color="isActive(item) ? 'success' : 'error'"
                :variant="isActive(item) ? 'outlined' : 'flat'"
            >{{ isActive(item) ? 'Activa' : 'Bloqueada' }}</v-chip>
        </template>

        <template #item.availability="{item}">
            <v-tooltip
                v-if="activationStartsSoon(item) || activationExpiresSoon(item)"
                :text="`${activationStartsSoon(item) ? 'Começa' : 'Expira'} em menos de 7 dias!`"
                location="top"
            >
                <template #activator="{props}">
                    <v-chip
                        v-bind="props"
                        :prepend-icon="
                            activationStartsSoon(item)
                            ? 'mdi-timer-alert-outline'
                            : 'mdi-alert'
                        "
                        variant="flat"
                        :color="
                            activationStartsSoon(item)
                            ? 'info'
                            : 'warning'
                        "
                    >{{ item?.starts_on}} → {{ item?.ends_on ?? '-'}}</v-chip>
                </template>
            </v-tooltip>
            <span v-else>
                {{ item?.starts_on}} → {{ item?.ends_on ?? '-'}}
            </span>
        </template>

        <template #item.actions={item}>
            <div class="d-flex flex-column flex-md-row justify-lg-end ga-4 align-center">
                <v-tooltip 
                    :text="
                        isActive(item)
                        ? 'Bloquear Avaliações'
                        : 'Ativar Avaliações'
                    " 
                    location="top"
                >
                    <template #activator="{props}">
                        <v-btn 
                            v-bind="props" 
                            :icon="isActive(item) ? 'mdi-lock-open-variant-outline' : 'mdi-lock-alert'" 
                            :color="isActive(item) ? 'error' : 'success'" 
                            rounded="xl" 
                            size="x-small" 
                            @click="
                                openDialogForAction(
                                    isActive(item) ? 'block' : 'activate',
                                    item
                                )
                            "></v-btn>
                    </template>
                </v-tooltip>
            </div>
        </template>
    </v-data-table-server>
    <div class="d-flex justify-end align-center">
        <strong v-if="/* !firstLoad */true">
            {{ filters.from ?? 1 }}
            -
            {{ filters.to ?? filters.per_page ?? 25 }}
            /
            {{ filters.total ?? "25" }}
        </strong>

        <v-pagination
            v-model="filters.page"
            :length="filters.last_page ?? 1"
            :total-visible="0"
            @update:model-value="fetchAssessmentStatusesList"
        />
    </div>
</template>

<script setup>
import { computed, onMounted, readonly, ref, resolveComponent } from 'vue';
import AssessmentActivation from '../../../api/assessments/manage/AssessmentActivation';
import { useDisplay } from 'vuetify';
import { getDate } from '../../../composables/datetime';
import actionReferences from '../../../composables/actionReferences';
import { useDialog } from '@internal_projects_dd/universal-dialog';
import AssessmentActivationStatusForm from './AssessmentActivationStatusForm.vue';
const props = defineProps({
    minimumYear: {
        type: [String,Number],
        default: 2000,
    },

    yearOffset: {
        type: [String, Number],
        default: 1,
    },
})

const assessmentTypesList = [
    {
        title: "Auto-Avaliações",
        value: "self",
    },
    {
        title: "Avaliações Ascendentes",
        value: "ascending",
    },
    {
        title: "Avaliações Descendentes",
        value: "downward",
    },
];

const tableHeaders = [
    {
        title: "Avaliação",
        sortable: true,
        align: "center",
        key: "type",
    },
    {
        title: "Ano",
        sortable: true,
        align: "center",
        key: "year",
    },
    {
        title: "Estado",
        key: "enabled",
        sortable: true,
        align: "center",
    },
    {
        title: "Disponível",
        key: "availability",
        align: "center",
        sortable: false,
    },
    {
        title: "Ações",
        key: "actions",
        sortable: false,
        align: "end",
    }
];

const {mdAndDown} = useDisplay();
const dialog = useDialog();

const today = new Date();
console.log(today, getDate(today));
const currentYear = today.getFullYear();

const activationStatusesList = ref([]);
const isFetchingActivationStatusesList = ref(false);

const filters = ref({
    type: null,
    page: 1,
    to: 1,
    from: 1,
    total: 1,
    per_page: 10,
    year: null,
    enabled: null,
    order_by: "year",
    direction: "desc"
});
const yearsList = ref(
    Array.from(
        Array(currentYear + Number(props.yearOffset)).keys()
    ).toReversed()
    .toSpliced(currentYear - Number(props.minimumYear) + Number(props.yearOffset) )
);
const localIsFetchingYearsList = ref(false);

function isExpired(activation_status) {
    if(!activation_status?.ends_on && activation_status?.enabled) {
        return true;
    } 

    if(activation_status?.ends_on
        && activation_status?.starts_on 
        && new Date(activation_status?.ends_on) <= today
        && new Date(activation_status?.starts_on) <= today
    ) {
        return true
    }

    return false;
    
    /* !activation_status?.enabled 
            || (
                getDate(today) > activation_status?.ends_on
            ) */
}

function isActive(activation_status) {
    const isActive = activation_status?.starts_on 
            && activation_status?.ends_on
            && new Date(activation_status?.starts_on) <= today
            && new Date(activation_status?.ends_on) >= today
            ;
    // console.log(
    //     activation_status?.starts_on, 
    //     activation_status?.ends_on,
    //     new Date(activation_status?.starts_on) <= today,
    //     new Date(activation_status?.ends_on) >= today,
    // );
    return isActive;
}

function activationExpiresSoon(activation_status, soonDays = 7) {
    return !isExpired(activation_status)
            && new Date(activation_status?.ends_on) < new Date(currentYear, today.getMonth(), today.getDate() + soonDays)
}

function activationStartsSoon(activation_status, soonDays = 7) {
    // console.log("starts soon:", activation_status);
    return !isExpired(activation_status)
            && !isActive(activation_status)
            && ! activationExpiresSoon(activation_status)
            && new Date(activation_status?.starts_on) <= new Date(currentYear, today.getMonth(), today.getDate() + soonDays)
}

async function fetchAssessmentStatusesList(){
    
    if(isFetchingActivationStatusesList.value) {
        return;
    }
    try {
        isFetchingActivationStatusesList.value = true;
        const response = await AssessmentActivation.index({...filters.value});

        activationStatusesList.value = 
            response?.data?.data
            ?? response?.data
            ?? [];

        filters.value = {
            ...filters.value,
            from: response?.data?.from ?? 1,
            to: response?.data?.to 
                ?? activationStatusesList.value?.length 
                ?? 1,

            total: response?.data?.total 
                    ?? activationStatusesList.value?.length 
                    ?? 1,
            page: response?.data?.current_page
                    ?? 1,
            
        }
    } catch (error) {
        
    } finally {
        isFetchingActivationStatusesList.value = false;
    }
}



async function fetchAvailableYears() {
    if(localIsFetchingYearsList.value) {
        return;
    }

    try {
        localIsFetchingYearsList.value = true;
        const response = await AssessmentActivation.getAvailableYears();

        const newYearsList = response?.data?.available_years 
                            ?? response?.data
                            ?? [];

        if(newYearsList?.length > 0) {
            yearsList.value = [
                ...newYearsList?.toSorted()?.toReversed()
            ];
        }

    } catch (error) {
        
    } finally {
        localIsFetchingYearsList.value = false;
    }
} 

async function openDialogForAction(action = "create", activation_status = null) {

    // actionReferences.throwOnIllegalCrudAction(action);

    const idempotency_key = self?.crypto?.randomUUID() ?? null;

    const componentProperties = resolveComponentPropertiesForAction(
        action,
        activation_status
    );

    componentProperties.idempotency_key = idempotency_key;

    const actionSpecificDialogOptions = resolveDialogOptionsForAction(
        action
    );

    let openedDialog = null;

    componentProperties.onActivationEnabledChange = (enabled) => {
        if (!openedDialog) {
            return;
        }

        Object.assign(
            openedDialog.options,
            resolveSubmitButtonOptions(action, enabled),
        );
    };


    const dialogPromise = dialog.open({
        component: AssessmentActivationStatusForm,
        props: componentProperties,
        options: actionSpecificDialogOptions,
    });

    openedDialog = dialog.state.stack.at(-1);

    await dialogPromise;


}

function resolveSubmitButtonOptions(action, enabled) {
    const isInitialCreateState =
        actionReferences.isValidCreateAction(action)
        && enabled === null;

    if (isInitialCreateState) {
        return {
            confirmActionText: "Definir estado de Avaliações",
            onConfirmActionText: "Definindo estado de Avaliações...",
            confirmActionIcon: "mdi-plus",
            confirmActionColor: "success",
            successMessageTitle: "Estado definido com sucesso!",
            failedMessageTitle: "Erro a definir estado" ,
        };
    }

    if (enabled === true) {
        return {
            confirmActionText: "Ativar Avaliações",
            onConfirmActionText: "Ativando Avaliações...",
            confirmActionIcon: "mdi-lock-open-variant-outline",
            confirmActionColor: "success",
            successTitle: "Avaliações ativadas",
            successMessage: "com sucesso!",
            failedMessageTitle: "Erro a ativar avaliações!"
        };
    }

    return {
        confirmActionText: "Bloquear Avaliações",
        onConfirmActionText: "Bloqueando Avaliações...",
        confirmActionIcon: "mdi-lock-alert",
        confirmActionColor: "error",
        successTitle: "Avaliações bloqueadas",
        successMessage: "com sucesso!",
        failedMessageTitle: "Erro a bloquear avaliações",
    };
}

function resolveDialogOptionsForAction(action, enabled = null) {
    const dialogOptions = {
        titleBackgroundColor: "transparent",
        titleTextColor: "primary",
        persistent: false,
        maxWidth: "500",
        showCancel: false,
        onConfirm: "submit",
        confirmActionColor: "primary",
        onConfirmFailureExpectedResult: false,
        onConfirmFailureThrow: true,
        ...resolveSubmitButtonOptions(action, enabled),
        
    };

    // returning early in get actions since it does not need any 
    // further options
    if (actionReferences.isValidGetAction(action)) {
        return dialogOptions;
    }

    // set shared dynamic properties    
    dialogOptions.onConfirmSuccess = async (result, currentDialog) => {
        fetchAssessmentStatusesList();

        await openSuccessDialog(
            (currentDialog?.options?.successTitle || currentDialog?.options?.successMessage)
            ? `${currentDialog?.options?.successTitle || ''} ${currentDialog?.options?.successMessage || ''}`
            : "Ação realizada com sucesso!"
        );

        return result;
    }

    dialogOptions.onConfirmError = async (error, currentDialog) => {
        console.error(
            currentDialog?.failedMessageTitle,
            String(error)
        );

        currentDialog.options.onConfirmFailedErrorTitle =
                    currentDialog?.failedMessageTitle
                    ?? error?.reason
                    ?? `Erro:`;

        currentDialog.options.onConfirmFailedErrorMessage = 
            (Object.values(error?.response?.data?.errors ?? {})?.flat().join("\n")
            || error?.response?.data?.message)
            ?? error?.message
            ?? String(error)
            ?? currentDialog.options?.failedMessage
            ?? `Não foi possível alterar o estado das ativações! Tente novamente`;
    }


    return dialogOptions;
}

function resolveComponentPropertiesForAction(
    action,
    activation_status = null
) {
    console.log(activation_status);
    const properties = {
        standalone: false,
        readonly: actionReferences.isValidReadonlyAction(action),
        activationStatusModel: {...(activation_status ?? {})},
        activationStatusId: activation_status?.id ?? null,
        yearsList : yearsList,
        isFetchingYearsList: localIsFetchingYearsList,
        // type: activation_status?.type,
    };

    if(["activate", "block"].includes(action)) {

        if(action == "block") {
            properties.activationStatusModel.enabled = false,
            // force the end date to null so the user has to input a value
            properties.activationStatusModel.ends_on = null;
        }
        else if (action == "activate") {
            properties.activationStatusModel.enabled = true;
            // properties.activationStatusModel.starts_on = ;
            // force the end date to null so the user has to input a value
            properties.activationStatusModel.ends_on = null;
        }

        properties.action = "update";
    }


    return properties;
}

async function openSuccessDialog(bodyText) {
    await dialog.open({
        options: {
            bodyText,
            persistent: true,
            maxWidth: 500,
            showCancel: false,
            confirmActionIcon: "",
            confirmActionText: "Ok",
            confirmActionColor: "primary",
        },
    });
}

function handleSortingUpdate(updatedSorting = []) {
    const sorting = Array.isArray(updatedSorting)
        ? updatedSorting[0]
        : updatedSorting;

    if (!sorting?.key || !sorting?.order) {
        return;
    }

    filters.value.order_by = sorting.key;
    filters.value.direction = sorting.order;

    fetchAssessmentStatusesList();
}

onMounted(() => {
    fetchAssessmentStatusesList();
    fetchAvailableYears();
})
</script>